import { type Context, createContext, useContext } from 'react';

import type { MediaProvider } from '@atlaskit/editor-common/provider-factory';
import type { AuthProvider } from '@atlaskit/media-core';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line no-restricted-imports
import { HelpHelpCenterAri, HelpPageAri } from '@atlassian/ari/help';

import type { HubContextType } from './types';

export const getAuthProvider =
	(asapIssuer: string, mediaToken: string, mediaUrl: string): AuthProvider =>
	async () => {
		return {
			asapIssuer,
			token: mediaToken,
			baseUrl: mediaUrl,
		};
	};

export const getMediaProvider = (
	mediaCollectionName: string,
	asapIssuer: string,
	mediaToken: string,
	mediaUrl: string,
	isUploadMediaConfigRequired?: boolean,
): MediaProvider => {
	const authProvider = getAuthProvider(asapIssuer, mediaToken, mediaUrl);
	return {
		uploadParams: {
			collection: mediaCollectionName,
		},
		viewMediaClientConfig: {
			authProvider,
			useSha256ForUploads: true,
		},
		...(isUploadMediaConfigRequired &&
			fg('enable-upload-media') && {
				uploadMediaClientConfig: {
					authProvider,
				},
			}),
	};
};

const getSafeHelpCenterIdMeta = (
	helpCenterAri: string,
): { activationId: string; helpCenterId: string } | undefined => {
	try {
		if (helpCenterAri && helpCenterAri !== 'null') {
			const { activationId, helpCenterId } = HelpHelpCenterAri.parse(helpCenterAri);
			return { activationId, helpCenterId };
		}
	} catch {
		// todo: add analytics
	}
};

export const getPageAri = (helpCenterAri: string, pageId: string): string => {
	const { activationId = '', helpCenterId = '' } = getSafeHelpCenterIdMeta(helpCenterAri) || {};
	return (
		HelpPageAri.create({
			activationId: activationId,
			pageId: pageId || helpCenterId,
		}).toString() || ''
	);
};

export const HubContext: Context<HubContextType | null> = createContext<HubContextType | null>(
	null,
);

export const useHubContext = (): HubContextType => {
	const context = useContext(HubContext);
	if (!context) {
		throw new Error('useHubContext must be used within a HubContextProvider');
	}
	return context;
};
